import React, { useState, useEffect } from "react";

const Footer = () => {
  const [currentYear] = useState(new Date().getFullYear());
  const [isVisible, setIsVisible] = useState(false);
  const [currentTime] = useState(new Date());

  // Trigger animation on mount
  useEffect(() => {
    setIsVisible(true);
  }, []);

  // Check if restaurant is currently open
  const isOpen = () => {
    const now = currentTime;
    const day = now.getDay();
    const hour = now.getHours();
    const minute = now.getMinutes();
    const currentMinutes = hour * 60 + minute;

    if (day === 0) {
      return currentMinutes >= 16 * 60 && currentMinutes < 21 * 60;
    } else if (day >= 1 && day <= 6) {
      return currentMinutes >= 5 * 60 && currentMinutes < 23 * 60;
    }
    return false;
  };

  // Scroll to sections
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const headerHeight = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerHeight;
      window.scrollTo({ top: offsetPosition, behavior: "smooth" });
    }
  };

  // Handle footer link clicks
  const handleFooterLinkClick = (link) => {
    switch (link) {
      case "Home":
        window.scrollTo({ top: 0, behavior: "smooth" });
        break;
      case "About":
        scrollToSection("about");
        break;
      case "Menu":
        scrollToSection("menu");
        break;
      case "Contact":
        const contactElement = document.getElementById("contact");
        if (contactElement) {
          const headerHeight = 80;
          const elementPosition = contactElement.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - headerHeight;
          window.scrollTo({ top: offsetPosition, behavior: "smooth" });
        }
        break;
      default:
        break;
    }
  };

  return (
    <>
      {/* Contact & Map Section */}
      <section id="contact" className="bg-white py-16 md:py-20">
        <div className="container mx-auto px-4 md:px-6">
          {/* Section Header */}
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Visit <span className="text-primary">Us</span>
            </h2>
            <p className="text-gray-600 text-lg md:text-xl max-w-2xl mx-auto">
              Find us in the heart of Ras Tanura and experience our culinary excellence
            </p>
          </div>

          {/* Map and Info Grid */}
          <div className="grid lg:grid-cols-3 gap-8 md:gap-10">
            {/* Google Maps */}
            <div className="lg:col-span-2">
              <div className="rounded-2xl overflow-hidden shadow-2xl h-64 sm:h-80 md:h-96 lg:h-[500px] relative">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3580.037679882226!2d50.16261531502954!3d26.20087098344494!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3e49ff5b2c5c5c5d%3A0x9b3a3c3c3c3c3c3c!2sRas%20Tanura%2C%20Saudi%20Arabia!5e0!3m2!1sen!2s!4v1647482400000!5m2!1sen!2s"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  className="absolute inset-0"
                  title="Food Palace Location in Ras Tanura"
                />
              </div>
            </div>

            {/* Contact Info & Hours */}
            <div className="space-y-8">
              {/* Contact Info Card */}
              <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-6 shadow-lg border border-gray-100 transform transition-all duration-500 hover:shadow-xl hover:-translate-y-1">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center group-hover:bg-primary/20 transition-all duration-300">
                    <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">Contact Info</h3>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div className="flex items-start gap-3 group">
                    <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-primary/20 transition-all duration-300">
                      <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-800 group-hover:text-primary transition-colors duration-300">Address</p>
                      <p className="text-gray-600">Ras Tanura</p>
                      <p className="text-gray-500 text-sm">Eastern Province, Saudi Arabia</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3 group">
                    <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-primary/20 transition-all duration-300">
                      <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-800 group-hover:text-primary transition-colors duration-300">Phone</p>
                      <p className="text-gray-600">+966 55 254 9510</p>
                    </div>
                  </div>
                  
                  
                </div>
              </div>

              {/* Opening Hours Card */}
              <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-6 shadow-lg border border-gray-100 transform transition-all duration-500 hover:shadow-xl hover:-translate-y-1">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                      <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">Opening Hours</h3>
                      <div className="flex items-center gap-2 mt-1">
                        <div className={`w-2 h-2 rounded-full ${isOpen() ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`} />
                        <span className={`text-sm font-medium ${isOpen() ? 'text-green-600' : 'text-red-600'}`}>
                          {isOpen() ? 'OPEN NOW' : 'CLOSED'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  {[
                    { days: "Mon - Thu", hours: "5:00 AM - 11:00 PM", highlight: false },
                    { days: "Fri - Sat", hours: "5:00 AM - 11:00 PM", highlight: false },
                    { days: "Sunday", hours: "4:00 PM - 9:00 PM", highlight: false },
                  ].map((schedule, index) => (
                    <div
                      key={index}
                      className={`flex justify-between items-center p-4 rounded-xl transition-all duration-300 hover:shadow-md group ${
                        schedule.highlight 
                          ? 'bg-primary/5 border border-primary/20' 
                          : 'bg-white border border-gray-100 hover:border-primary/30'
                      }`}
                    >
                      <span className={`font-medium ${schedule.highlight ? 'text-primary' : 'text-gray-800'}`}>
                        {schedule.days}
                      </span>
                      <span className={`font-semibold ${schedule.highlight ? 'text-primary' : 'text-gray-700'}`}>
                        {schedule.hours}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer Section */}
      <footer className="bg-gradient-to-b from-gray-900 to-black text-white overflow-hidden">
        <div className="h-1 bg-gradient-to-r from-primary via-white/30 to-secondary" />
        
        <div className="container mx-auto px-4 md:px-6 py-12 md:py-16">
          <div className={`grid md:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-10 mb-8 md:mb-12 transition-all duration-1000 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}>
            {/* Brand Section */}
            <div className="md:col-span-2 lg:col-span-1">
              <div className="mb-6 group">
                <h3 className="text-3xl md:text-4xl font-bold mb-4">
                  Food<span className="text-primary">Palace</span>
                </h3>
                <div className="w-16 h-1 bg-gradient-to-r from-primary to-secondary mt-2 group-hover:w-24 transition-all duration-500" />
              </div>
              <p className="text-gray-300 mb-6 leading-relaxed">
                Experience the perfect blend of traditional flavors and modern culinary artistry.
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="text-xl font-semibold mb-6 pb-2 border-b border-white/10">Quick Links</h4>
              <div className="space-y-3">
                {["Home", "Menu", "Contact"].map((link) => (
                  <button
                    key={link}
                    onClick={() => handleFooterLinkClick(link)}
                    className="block text-gray-300 hover:text-white transition-all duration-300 hover:translate-x-2 py-1 text-left w-full"
                  >
                    {link}
                  </button>
                ))}
              </div>
            </div>

            {/* Useful Links */}
            <div>
              <h4 className="text-xl font-semibold mb-6 pb-2 border-b border-white/10">Useful Links</h4>
              <div className="space-y-3">
                {["Privacy Policy", "Terms of Service",  "FAQ"].map((link) => (
                  <button
                    key={link}
                    className="block text-gray-300 hover:text-white transition-all duration-300 hover:translate-x-2 py-1 text-left w-full"
                  >
                    {link}
                  </button>
                ))}
              </div>
            </div>

            {/* Contact Details */}
            <div>
              <h4 className="text-xl font-semibold mb-6 pb-2 border-b border-white/10">Contact Details</h4>
              <div className="space-y-4">
                {/* <div className="flex items-start gap-3 group">
                  <div className="w-8 h-8 bg-primary/20 rounded-full flex items-center justify-center flex-shrink-0 group-hover:bg-primary/30 transition-all duration-300">
                    <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-medium text-white group-hover:text-primary transition-colors duration-300">Address</p>
                    <p className="text-gray-300 text-sm group-hover:text-gray-200 transition-colors duration-300">Ras Tanura, Saudi Arabia</p>
                  </div>
                </div> */}
                <div className="flex items-start gap-3 group">
                  <div className="w-8 h-8 bg-primary/20 rounded-full flex items-center justify-center flex-shrink-0 group-hover:bg-primary/30 transition-all duration-300">
                    <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-medium text-white group-hover:text-primary transition-colors duration-300">Phone</p>
                    <p className="text-gray-300 text-sm group-hover:text-gray-200 transition-colors duration-300">+966 55 254 9510</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 group">
                  <div className="w-8 h-8 bg-primary/20 rounded-full flex items-center justify-center flex-shrink-0 group-hover:bg-primary/30 transition-all duration-300">
                    <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-medium text-white group-hover:text-primary transition-colors duration-300">Email</p>
                    <p className="text-gray-300 text-sm group-hover:text-gray-200 transition-colors duration-300">info@foodpalace.com</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="border-t border-white/10 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center gap-6">
              <div className="text-center md:text-left">
                <p className="text-gray-400 text-sm md:text-base">
                  © {currentYear} Food Palace. All rights reserved.
                </p>
                <p className="text-gray-500 text-xs md:text-sm mt-1">
                  Crafted with ❤️ for food lovers
                </p>
              </div>
              
              <button
                onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center text-white hover:bg-primary hover:scale-110 transition-all duration-300"
                aria-label="Scroll to top"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 10l7-7m0 0l7 7m-7-7v18" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
};

export default Footer;